import {
  ApiType,
  isApiType,
  getApiBasePath,
} from '@modules/apiReference/page/sharedUI/utils/linkableTypeReferenceUtils';

const getExplicitApiTypePrefixIfAny = (apiType: ApiType | string): string | undefined => {
  let explicitType: string | undefined;
  if (isApiType(apiType) && apiType === 'Enum') {
    explicitType = 'Enum';
  }
  if (explicitType) return `${explicitType}.`;
  return undefined;
};

function resolveLinkFromInlineCodeWithoutDefaultText(input: string) {
  const nameMatch = /^(\w+)\.(\w+)$/.exec(input);
  // 'Class.Instance' or
  // 'Enum.Material'
  if (nameMatch) {
    // ['Class.Instance', 'Class', 'Instance'] or
    // ['Enum.Material', 'Enum', 'Material']
    const apiType = nameMatch[1];
    const explicitApiTypePrefixIfAny = getExplicitApiTypePrefixIfAny(apiType);
    const apiName = nameMatch[2];
    const basePath = getApiBasePath(apiType, apiName);
    if (basePath) {
      return {
        link: `${basePath}/${apiName}`,
        text: `${explicitApiTypePrefixIfAny ?? ''}${apiName}`,
      };
    }
  }

  const memberMatch = /^(\w+)\.(\w+)([.|:])(\w+)(\(.*\))?$/.exec(input);
  // [1] Class.SomeInstance.SomeName or
  // [2] Class.SomeInstance:SomeMethod or
  // [3] Class.SomeInstance:SomeMethod(args...) or
  if (memberMatch) {
    // ['Class.SomeInstance.SomeName', 'Class', 'SomeInstance', '.', 'SomeName', PARENS]
    // where PARENS = undefined [1][2] or '(args...)' [3]
    const [
      ,
      apiType, // 'Class', 'Datatype', etc.
      apiName, // 'SomeInstance'
      apiNotation, // '.' for member or ':' for method
      apiMember, // 'SomeName'
      apiMemberParens, // '()' or '(args...)' for method
    ] = memberMatch;
    const basePath = getApiBasePath(apiType, apiName, apiMember);
    const isFuncOrMethod = apiNotation === ':' || apiMemberParens?.length > 0;
    if (basePath) {
      const paramsIfAny = isFuncOrMethod ? (apiMemberParens ?? '()') : '';
      const explicitApiTypePrefixIfAny = getExplicitApiTypePrefixIfAny(apiType);
      const displayText =
        apiType === 'Global'
          ? `${apiMember}${paramsIfAny}`
          : `${explicitApiTypePrefixIfAny ?? ''}${apiName}${apiNotation}${apiMember}${paramsIfAny}`;
      return {
        link: `${basePath}/${apiName}#${apiMember}`,
        text: displayText,
      };
    }
  }

  return null;
}

export default function resolveLinkFromInlineCodeText(input: string | undefined) {
  const defaultResult = {
    link: null,
    text: input,
  };
  if (!input) {
    return defaultResult;
  }
  const noLinkMatch = /^(.*)\|no-link$/i.exec(input); // Font|no-link
  if (noLinkMatch) {
    return {
      link: null,
      text: noLinkMatch[1],
    };
  }

  const splitsByPipe = input?.split('|');
  if (splitsByPipe.length > 2) {
    return defaultResult;
  }
  const [codeText, customDisplayText] = splitsByPipe;
  const result = resolveLinkFromInlineCodeWithoutDefaultText(codeText);
  if (!result) {
    return defaultResult;
  }
  return {
    ...result,
    text: customDisplayText ?? result.text,
  };
}
